import { useState } from 'react'
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Progress,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd'
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ExperimentOutlined,
  FileTextOutlined,
  MinusCircleOutlined,
  ReloadOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title, Text } = Typography

// ── Types ─────────────────────────────────────────────────────────────────────

interface ParamRow {
  name: string
  unit: string
  spec_min: string | null
  spec_max: string | null
  parakh_avg: string | null
  parakh_result: string | null
  received_avg: string | null
  received_result: string | null
  overall_result: string | null
}

interface ParakhLotSummary {
  lot_id: string
  ginner_lot_no: string
  pr_no: string
  ginner_name: string | null
  total_bales: number | null
  avg_moisture: string | null
  mic_value: string | null
  test_result: string
  hvi_results: Record<string, unknown> | null
}

interface LotWiseHVI {
  party_lot_no: string
  mill_lot_no: string | null
  bale_count: number
  sampled_count: number
  avg_mic: string | null
  avg_staple: string | null
  avg_strength: string | null
  avg_uniformity: string | null
  avg_moisture: string | null
  avg_trash: string | null
  hvi_complete: boolean
  qc_approved: number
  qc_pending: number
  qc_hold: number
  qc_rejected: number
}

interface GRNComparison {
  grn_id: string
  grn_no: string
  grn_date: string | null
  do_no: string
  po_no: string
  supplier_name: string
  qc_assigned_at: string | null
  total_bales: number
  bales_pending: number
  bales_approved: number
  bales_hold: number
  bales_rejected: number
  hvi_data_complete: boolean
  parameters: ParamRow[]
  parakh_lots: ParakhLotSummary[]
  lot_wise_hvi: LotWiseHVI[]
  overall_status: string
}

interface Bale {
  id: string
  bale_no: string
  weight_kg: string
  lot_no: string | null
  micronaire: string | null
  staple_length_mm: string | null
  strength_g_tex: string | null
  uniformity_index: string | null
  sfi: string | null
  moisture_pct: string | null
  trash_pct: string | null
  rd_value: string | null
  plus_b: string | null
  sci: string | null
  elongation_pct: string | null
  color_grade: string | null
  qc_status: string
  qc_remarks: string | null
  category_band: string | null
  godown: string | null
}

interface GRNSummary {
  id: string
  grn_no: string
  grn_date: string
  do_no: string
  po_no: string
  supplier_name: string
  physical_bale_count: number
  total_weight_kg: string
  qc_assigned_at: string | null
  bales: Bale[]
}

// ── Result Tag ─────────────────────────────────────────────────────────────────

function ResultTag({ result }: { result: string | null }) {
  if (!result || result === 'PENDING')
    return <Tag icon={<ClockCircleOutlined />} color="orange">Pending</Tag>
  if (result === 'PASS')
    return <Tag icon={<CheckCircleOutlined />} color="green">Pass</Tag>
  if (result === 'FAIL')
    return <Tag icon={<CloseCircleOutlined />} color="red">Fail</Tag>
  if (result === 'N/A')
    return <Tag color="default">N/A</Tag>
  return <Tag>{result}</Tag>
}

function qcStatusColor(s: string) {
  return s === 'APPROVED' ? 'green' : s === 'HOLD' ? 'gold' : s === 'REJECTED' ? 'red' : 'orange'
}

function overallBadge(status: string) {
  if (status === 'PASS') return <Badge status="success" text={<Text style={{ color: '#52c41a', fontWeight: 600 }}>PASS</Text>} />
  if (status === 'FAIL') return <Badge status="error" text={<Text style={{ color: '#ff4d4f', fontWeight: 600 }}>FAIL</Text>} />
  return <Badge status="processing" text={<Text style={{ color: '#fa8c16', fontWeight: 600 }}>PENDING</Text>} />
}

// ── Bale HVI Entry Modal ──────────────────────────────────────────────────────

function BaleHVIModal({
  bale, open, onClose, onSaved,
}: { bale: Bale | null; open: boolean; onClose: () => void; onSaved: () => void }) {
  const [form] = Form.useForm()
  const mut = useMutation({
    mutationFn: (d: object) => apiClient.patch(`/bales/${bale!.id}`, d).then(r => r.data),
    onSuccess: () => { message.success('HVI saved'); onSaved(); onClose() },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Save failed'),
  })
  if (!bale) return null
  return (
    <Modal
      title={<Space><ExperimentOutlined />HVI Parameters — {bale.bale_no}</Space>}
      open={open} onCancel={onClose}
      onOk={() => form.validateFields().then(v => mut.mutate(v))}
      okText="Save" confirmLoading={mut.isPending} width={640}
      afterOpenChange={v => v && form.setFieldsValue({ ...bale })}
    >
      <Form form={form} layout="vertical" size="small">
        <Row gutter={12}>
          {[
            ['micronaire', 'Micronaire (MIC)', 2],
            ['staple_length_mm', 'Staple Length (mm)', 2],
            ['strength_g_tex', 'Strength (g/tex)', 2],
            ['uniformity_index', 'Uniformity (%)', 2],
            ['sfi', 'Short Fiber Index', 2],
            ['moisture_pct', 'Moisture (%)', 2],
            ['trash_pct', 'Trash (%)', 2],
            ['rd_value', 'Color Rd', 2],
            ['plus_b', 'Color +b', 2],
            ['elongation_pct', 'Elongation (%)', 2],
            ['sci', 'SCI', 0],
          ].map(([name, label, prec]) => (
            <Col span={8} key={String(name)}>
              <Form.Item name={String(name)} label={String(label)}>
                <InputNumber style={{ width: '100%' }} precision={Number(prec)} step={0.1} />
              </Form.Item>
            </Col>
          ))}
          <Col span={8}>
            <Form.Item name="color_grade" label="Color Grade">
              <Input placeholder="e.g. 31-4" />
            </Form.Item>
          </Col>
        </Row>
        <Divider />
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name="qc_status" label="QC Decision">
              <Select options={[
                { value: 'PENDING', label: 'Pending' },
                { value: 'APPROVED', label: 'Approved' },
                { value: 'HOLD', label: 'Hold' },
                { value: 'REJECTED', label: 'Rejected' },
              ]} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="category_band" label="Category Band">
              <Input placeholder="CAT-A" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="godown" label="Godown">
              <Input placeholder="G1" />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="qc_remarks" label="Remarks">
              <Input.TextArea rows={2} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}

// ── Comparison Drawer ────────────────────────────────────────────────────────

function ComparisonDrawer({
  grn, open, onClose,
}: { grn: GRNSummary | null; open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const [hviBale, setHviBale] = useState<Bale | null>(null)

  const { data: comp, isLoading, refetch } = useQuery<GRNComparison>({
    queryKey: ['grn-lab-comparison', grn?.id],
    queryFn: () => apiClient.get(`/grn/${grn!.id}/lab-comparison`).then(r => r.data),
    enabled: !!grn?.id && open,
  })

  const bulkMut = useMutation({
    mutationFn: ({ action, remarks }: { action: string; remarks?: string }) =>
      apiClient.post(`/grn/${grn!.id}/bulk-qc`, { action, remarks }).then(r => r.data),
    onSuccess: (_, vars) => {
      message.success(`All bales marked ${vars.action}`)
      qc.invalidateQueries({ queryKey: ['grn-list'] })
      refetch()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  if (!grn) return null

  // Comparison table columns
  const paramCols = [
    {
      title: 'Parameter',
      dataIndex: 'name',
      key: 'name',
      width: 160,
      render: (v: string, r: ParamRow) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ fontSize: 12 }}>{v}</Text>
          {r.unit && <Text type="secondary" style={{ fontSize: 10 }}>{r.unit}</Text>}
        </Space>
      ),
    },
    {
      title: () => (
        <Space direction="vertical" size={0} style={{ textAlign: 'center' as const }}>
          <Text style={{ fontSize: 11, color: '#1677ff', fontWeight: 600 }}>PO Spec</Text>
          <Text type="secondary" style={{ fontSize: 10 }}>Purchase Limit</Text>
        </Space>
      ),
      key: 'spec',
      width: 110,
      render: (_: unknown, r: ParamRow) => (
        <Text style={{ fontSize: 12, fontFamily: 'monospace' }}>
          {r.spec_min && r.spec_max
            ? `${r.spec_min} – ${r.spec_max}`
            : r.spec_min
            ? `≥ ${r.spec_min}`
            : r.spec_max
            ? `≤ ${r.spec_max}`
            : <Text type="secondary">—</Text>}
        </Text>
      ),
    },
    {
      title: () => (
        <Space direction="vertical" size={0} style={{ textAlign: 'center' as const }}>
          <Text style={{ fontSize: 11, color: '#722ed1', fontWeight: 600 }}>Parakh</Text>
          <Text type="secondary" style={{ fontSize: 10 }}>Pre-Shipment</Text>
        </Space>
      ),
      key: 'parakh',
      width: 120,
      render: (_: unknown, r: ParamRow) => (
        <Space direction="vertical" size={2}>
          <Text style={{ fontSize: 13, fontFamily: 'monospace', fontWeight: 600 }}>
            {r.parakh_avg ?? '—'}
          </Text>
          <ResultTag result={r.parakh_result} />
        </Space>
      ),
    },
    {
      title: () => (
        <Space direction="vertical" size={0} style={{ textAlign: 'center' as const }}>
          <Text style={{ fontSize: 11, color: '#389e0d', fontWeight: 600 }}>Received</Text>
          <Text type="secondary" style={{ fontSize: 10 }}>Warehouse Avg</Text>
        </Space>
      ),
      key: 'received',
      width: 120,
      render: (_: unknown, r: ParamRow) => (
        <Space direction="vertical" size={2}>
          <Text style={{ fontSize: 13, fontFamily: 'monospace', fontWeight: 600 }}>
            {r.received_avg ?? '—'}
          </Text>
          <ResultTag result={r.received_result} />
        </Space>
      ),
    },
    {
      title: 'Overall',
      key: 'overall',
      width: 90,
      render: (_: unknown, r: ParamRow) => <ResultTag result={r.overall_result} />,
    },
  ]

  // Bale table in drawer
  const baleCols = [
    {
      title: 'Bale No',
      dataIndex: 'bale_no',
      key: 'bale_no',
      render: (v: string) => <Text style={{ fontFamily: 'monospace', fontSize: 11 }} strong>{v}</Text>,
    },
    { title: 'Lot', dataIndex: 'lot_no', key: 'lot_no', render: (v: string) => v || '—' },
    {
      title: 'MIC / Staple',
      key: 'hvi1',
      render: (_: unknown, b: Bale) =>
        b.micronaire
          ? <Text style={{ fontSize: 11 }}>{b.micronaire} / {b.staple_length_mm ?? '—'}</Text>
          : <Text type="secondary" style={{ fontSize: 11 }}>No HVI</Text>,
    },
    {
      title: 'Moisture / Trash',
      key: 'hvi2',
      render: (_: unknown, b: Bale) =>
        b.moisture_pct
          ? <Text style={{ fontSize: 11 }}>{b.moisture_pct}% / {b.trash_pct ?? '—'}%</Text>
          : <Text type="secondary" style={{ fontSize: 11 }}>—</Text>,
    },
    {
      title: 'QC',
      dataIndex: 'qc_status',
      key: 'qc',
      render: (v: string) => <Tag color={qcStatusColor(v)} style={{ fontSize: 10 }}>{v}</Tag>,
    },
    {
      title: '',
      key: 'act',
      render: (_: unknown, b: Bale) => (
        <Button size="small" icon={<ExperimentOutlined />} onClick={() => setHviBale(b)}>
          HVI
        </Button>
      ),
    },
  ]

  const progressPct = grn.physical_bale_count > 0
    ? Math.round(((grn.bales.filter(b => b.qc_status !== 'PENDING').length) / grn.physical_bale_count) * 100)
    : 0

  return (
    <Drawer
      title={
        <Space>
          <ExperimentOutlined style={{ color: '#1677ff' }} />
          <span>Lab Results — {grn.grn_no}</span>
          {comp && overallBadge(comp.overall_status)}
        </Space>
      }
      open={open} onClose={onClose} width={860}
      extra={
        <Space>
          <Button size="small" icon={<ReloadOutlined />} onClick={() => refetch()}>Refresh</Button>
          <Popconfirm title="Approve all bales?" onConfirm={() => bulkMut.mutate({ action: 'APPROVE' })}>
            <Button size="small" type="primary" icon={<CheckCircleOutlined />}
              loading={bulkMut.isPending}>Approve All</Button>
          </Popconfirm>
          <Popconfirm title="Put all bales on Hold?" onConfirm={() => bulkMut.mutate({ action: 'HOLD' })}>
            <Button size="small" icon={<MinusCircleOutlined />} loading={bulkMut.isPending}>Hold All</Button>
          </Popconfirm>
        </Space>
      }
    >
      {comp && (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">

          {/* Header info */}
          <Card size="small">
            <Row gutter={16}>
              <Col span={6}>
                <Statistic title="Total Bales" value={comp.total_bales} valueStyle={{ fontSize: 20 }} />
              </Col>
              <Col span={6}>
                <Statistic title="Approved" value={comp.bales_approved}
                  valueStyle={{ fontSize: 20, color: '#52c41a' }} />
              </Col>
              <Col span={6}>
                <Statistic title="Hold" value={comp.bales_hold}
                  valueStyle={{ fontSize: 20, color: '#fa8c16' }} />
              </Col>
              <Col span={6}>
                <Statistic title="Rejected" value={comp.bales_rejected}
                  valueStyle={{ fontSize: 20, color: '#ff4d4f' }} />
              </Col>
            </Row>
            <div style={{ marginTop: 12 }}>
              <Text type="secondary" style={{ fontSize: 11 }}>QC Progress</Text>
              <Progress percent={progressPct} size="small"
                status={comp.overall_status === 'FAIL' ? 'exception' : undefined} />
            </div>
          </Card>

          {!comp.hvi_data_complete && (
            <Alert type="warning" showIcon icon={<WarningOutlined />}
              message="No HVI data entered yet. Click 'HVI' on any bale below or use bulk entry to record lab results." />
          )}

          {/* 3-way comparison table */}
          <Card
            size="small"
            title={
              <Space>
                <FileTextOutlined />
                <span>3-Way Parameter Comparison</span>
                <Tag color="blue">PO Spec</Tag>
                <Tag color="purple">Parakh Pre-Shipment</Tag>
                <Tag color="green">Received Warehouse</Tag>
              </Space>
            }
          >
            <Table
              dataSource={comp.parameters}
              columns={paramCols}
              rowKey="name"
              pagination={false}
              size="small"
              loading={isLoading}
              rowClassName={(r) =>
                r.overall_result === 'FAIL' ? 'param-fail'
                  : r.overall_result === 'PASS' ? 'param-pass' : ''
              }
            />
          </Card>

          {/* Parakh lot summaries */}
          {comp.parakh_lots.length > 0 && (
            <Card
              size="small"
              title={<><Tag color="purple">Parakh Lots in this DO</Tag>{comp.parakh_lots.length} lot(s)</>}
            >
              <Table
                dataSource={comp.parakh_lots}
                rowKey="lot_id"
                size="small"
                pagination={false}
                columns={[
                  { title: 'Ginner Lot No', dataIndex: 'ginner_lot_no', key: 'gln',
                    render: (v: string) => <Text strong style={{ fontFamily: 'monospace' }}>{v}</Text> },
                  { title: 'PR No', dataIndex: 'pr_no', key: 'pr' },
                  { title: 'Ginner', dataIndex: 'ginner_name', key: 'gn',
                    render: (v: string) => v || '—' },
                  { title: 'Bales', dataIndex: 'total_bales', key: 'tb',
                    render: (v: number) => v ? <Tag color="blue">{v}</Tag> : '—' },
                  { title: 'Avg Moisture', dataIndex: 'avg_moisture', key: 'am',
                    render: (v: string) => v ? `${v}%` : '—' },
                  { title: 'MIC', dataIndex: 'mic_value', key: 'mic',
                    render: (v: string) => v || '—' },
                  { title: 'QC Result', dataIndex: 'test_result', key: 'res',
                    render: (v: string) => (
                      <Tag color={v === 'PASS' ? 'green' : v === 'FAIL' ? 'red' : 'orange'}>{v}</Tag>
                    ) },
                  {
                    title: 'HVI (Pre-ship)',
                    key: 'hvi',
                    render: (_: unknown, r: ParakhLotSummary) => {
                      if (!r.hvi_results) return <Text type="secondary" style={{ fontSize: 11 }}>—</Text>
                      const h = r.hvi_results as Record<string, unknown>
                      return (
                        <Text style={{ fontSize: 11 }}>
                          {h.staple_length_mm ? `SL:${h.staple_length_mm}` : ''}
                          {h.strength ? ` Str:${h.strength}` : ''}
                        </Text>
                      )
                    },
                  },
                ]}
              />
            </Card>
          )}

          {/* Lot-wise HVI Summary */}
          {(comp.lot_wise_hvi?.length ?? 0) > 0 && (
            <Card
              size="small"
              title={
                <Space>
                  <Tag color="purple">Lot-wise HVI Summary</Tag>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Average per Party Lot No
                  </Text>
                </Space>
              }
            >
              <Table
                dataSource={comp.lot_wise_hvi || []}
                rowKey="party_lot_no"
                size="small"
                pagination={false}
                columns={[
                  {
                    title: 'Party Lot',
                    key: 'lot',
                    render: (_: unknown, r: LotWiseHVI) => (
                      <Space direction="vertical" size={0}>
                        <Text strong style={{ color: '#531dab' }}>Lot {r.party_lot_no}</Text>
                        {r.mill_lot_no && (
                          <Text style={{ fontFamily: 'monospace', fontSize: 10, color: '#722ed1' }}>
                            {r.mill_lot_no}
                          </Text>
                        )}
                      </Space>
                    ),
                  },
                  {
                    title: 'Bales',
                    key: 'bales',
                    render: (_: unknown, r: LotWiseHVI) => (
                      <Space direction="vertical" size={0}>
                        <Tag color="blue">{r.bale_count}</Tag>
                        {r.sampled_count > 0 && (
                          <Text type="secondary" style={{ fontSize: 10 }}>{r.sampled_count} sampled</Text>
                        )}
                      </Space>
                    ),
                  },
                  {
                    title: 'QC Status',
                    key: 'qc',
                    render: (_: unknown, r: LotWiseHVI) => (
                      <Space size={2} wrap>
                        {r.qc_approved > 0 && <Tag color="green" style={{ fontSize: 10 }}>✓ {r.qc_approved}</Tag>}
                        {r.qc_hold > 0 && <Tag color="gold" style={{ fontSize: 10 }}>⏸ {r.qc_hold}</Tag>}
                        {r.qc_rejected > 0 && <Tag color="red" style={{ fontSize: 10 }}>✗ {r.qc_rejected}</Tag>}
                        {r.qc_pending > 0 && <Tag color="orange" style={{ fontSize: 10 }}>⋯ {r.qc_pending}</Tag>}
                      </Space>
                    ),
                  },
                  {
                    title: 'MIC',
                    dataIndex: 'avg_mic',
                    key: 'mic',
                    align: 'right' as const,
                    render: (v: string | null) => v ?? <Text type="secondary">—</Text>,
                  },
                  {
                    title: 'Staple (mm)',
                    dataIndex: 'avg_staple',
                    key: 'sl',
                    align: 'right' as const,
                    render: (v: string | null) => v ?? <Text type="secondary">—</Text>,
                  },
                  {
                    title: 'Strength',
                    dataIndex: 'avg_strength',
                    key: 'str',
                    align: 'right' as const,
                    render: (v: string | null) => v ?? <Text type="secondary">—</Text>,
                  },
                  {
                    title: 'Unif %',
                    dataIndex: 'avg_uniformity',
                    key: 'uni',
                    align: 'right' as const,
                    render: (v: string | null) => v ?? <Text type="secondary">—</Text>,
                  },
                  {
                    title: 'Moisture %',
                    dataIndex: 'avg_moisture',
                    key: 'moi',
                    align: 'right' as const,
                    render: (v: string | null) => v ?? <Text type="secondary">—</Text>,
                  },
                  {
                    title: 'Trash %',
                    dataIndex: 'avg_trash',
                    key: 'tr',
                    align: 'right' as const,
                    render: (v: string | null) => v ?? <Text type="secondary">—</Text>,
                  },
                  {
                    title: 'HVI',
                    dataIndex: 'hvi_complete',
                    key: 'hvi',
                    render: (v: boolean) => v
                      ? <Tag color="green" icon={<CheckCircleOutlined />}>Done</Tag>
                      : <Tag color="orange">Pending</Tag>,
                  },
                ]}
              />
            </Card>
          )}

          {/* Bale-level HVI entry */}
          <Card
            size="small"
            title={<><ExperimentOutlined /> Bale-wise Lab Entry — {grn.physical_bale_count} bales</>}
          >
            <Table
              dataSource={grn.bales}
              columns={baleCols}
              rowKey="id"
              size="small"
              pagination={{ pageSize: 20, size: 'small' }}
              rowClassName={(b: Bale) =>
                b.qc_status === 'REJECTED' ? 'row-rejected'
                  : b.qc_status === 'APPROVED' ? 'row-approved'
                  : b.qc_status === 'HOLD' ? 'row-hold' : ''
              }
            />
          </Card>
        </Space>
      )}

      <BaleHVIModal
        bale={hviBale} open={!!hviBale} onClose={() => setHviBale(null)}
        onSaved={() => { refetch(); qc.invalidateQueries({ queryKey: ['grn-list'] }) }}
      />

      <style>{`
        .param-fail td { background: #fff2f0 !important; }
        .param-pass td { background: #f6ffed !important; }
        .row-rejected td { background: #fff2f0 !important; }
        .row-approved td { background: #f6ffed !important; }
        .row-hold td { background: #fffbe6 !important; }
      `}</style>
    </Drawer>
  )
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function GRNLabResultsPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const [selectedGRN, setSelectedGRN] = useState<GRNSummary | null>(null)

  const { data: grns = [], isLoading } = useQuery<GRNSummary[]>({
    queryKey: ['grn-list', plantId],
    queryFn: () => apiClient.get('/grn', { params: { plant_id: plantId } }).then(r => r.data),
    enabled: !!plantId,
  })

  // Compute QC progress per GRN from bales
  function grnProgress(grn: GRNSummary) {
    if (!grn.bales || grn.bales.length === 0) return null
    const total = grn.bales.length
    const done = grn.bales.filter(b => b.qc_status !== 'PENDING').length
    const passed = grn.bales.filter(b => b.qc_status === 'APPROVED').length
    const failed = grn.bales.filter(b => b.qc_status === 'REJECTED').length
    const hold = grn.bales.filter(b => b.qc_status === 'HOLD').length
    const hviDone = grn.bales.filter(b => b.micronaire != null).length
    return { total, done, passed, failed, hold, hviDone, pct: Math.round((done / total) * 100) }
  }

  const columns = [
    {
      title: 'GRN No',
      key: 'grn_no',
      render: (_: unknown, r: GRNSummary) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ fontFamily: 'monospace' }}>{r.grn_no}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {dayjs(r.grn_date).format('DD MMM YYYY')}
          </Text>
        </Space>
      ),
    },
    {
      title: 'DO / PO',
      key: 'dopo',
      render: (_: unknown, r: GRNSummary) => (
        <Space direction="vertical" size={0}>
          {r.do_no ? <Tag color="geekblue" style={{ fontSize: 11 }}>{r.do_no}</Tag> : null}
          {r.po_no ? <Text type="secondary" style={{ fontSize: 11 }}>PO: {r.po_no}</Text> : null}
        </Space>
      ),
    },
    {
      title: 'Supplier',
      dataIndex: 'supplier_name',
      key: 'supplier',
      render: (v: string) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Bales / Weight',
      key: 'qty',
      render: (_: unknown, r: GRNSummary) => (
        <Space direction="vertical" size={0}>
          <Tag color="blue">{r.physical_bale_count} bales</Tag>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {Number(r.total_weight_kg).toLocaleString('en-IN', { minimumFractionDigits: 0 })} kg
          </Text>
        </Space>
      ),
    },
    {
      title: 'QC Status',
      key: 'qc',
      width: 180,
      render: (_: unknown, r: GRNSummary) => {
        const p = grnProgress(r)
        if (!p) return <Tag color="orange">No Bales</Tag>
        return (
          <Space direction="vertical" size={2}>
            <Space size={4}>
              {p.passed > 0 && <Tag color="green" style={{ fontSize: 10 }}>✓ {p.passed}</Tag>}
              {p.hold > 0 && <Tag color="gold" style={{ fontSize: 10 }}>⏸ {p.hold}</Tag>}
              {p.failed > 0 && <Tag color="red" style={{ fontSize: 10 }}>✗ {p.failed}</Tag>}
              {p.done < p.total && (
                <Tag color="orange" style={{ fontSize: 10 }}>⏳ {p.total - p.done}</Tag>
              )}
            </Space>
            <Tooltip title={`${p.hviDone} of ${p.total} bales have HVI data`}>
              <Progress
                percent={p.pct}
                size="small"
                style={{ width: 120 }}
                status={p.failed > 0 ? 'exception' : p.pct === 100 ? 'success' : 'active'}
              />
            </Tooltip>
          </Space>
        )
      },
    },
    {
      title: 'QC Assigned',
      dataIndex: 'qc_assigned_at',
      key: 'qca',
      render: (v: string | null) =>
        v
          ? <Tag color="blue" icon={<ExperimentOutlined />} style={{ fontSize: 10 }}>
              {dayjs(v).format('DD MMM HH:mm')}
            </Tag>
          : <Tag color="orange">Not Assigned</Tag>,
    },
    {
      title: '',
      key: 'action',
      width: 120,
      render: (_: unknown, r: GRNSummary) => (
        <Button
          type="primary"
          size="small"
          icon={<ExperimentOutlined />}
          onClick={() => setSelectedGRN(r)}
        >
          Lab Results
        </Button>
      ),
    },
  ]

  const pendingQC = grns.filter(g => grnProgress(g)?.done !== grnProgress(g)?.total).length
  const allApproved = grns.filter(g => {
    const p = grnProgress(g)
    return p && p.total > 0 && p.passed === p.total
  }).length

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            <ExperimentOutlined style={{ color: '#1677ff', marginRight: 8 }} />
            GRN Lab Results — QC
          </Title>
          <Text type="secondary">
            3-way comparison: PO Purchase Spec · Parakh Pre-Shipment · Warehouse Received
          </Text>
        </Col>
        <Col>
          <Select
            value={plantId}
            onChange={setPlantId}
            style={{ width: 180 }}
            options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))}
          />
        </Col>
      </Row>

      {/* KPIs */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Total GRNs" value={grns.length} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="QC Pending" value={pendingQC}
              valueStyle={{ color: pendingQC > 0 ? '#fa8c16' : '#52c41a' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="All Bales Approved" value={allApproved}
              valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="GRNs with HVI Data"
              value={grns.filter(g => g.bales.some(b => b.micronaire != null)).length} />
          </Card>
        </Col>
      </Row>

      <Card>
        <Table
          dataSource={grns}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          size="small"
          pagination={{ pageSize: 20 }}
        />
      </Card>

      <ComparisonDrawer
        grn={selectedGRN}
        open={!!selectedGRN}
        onClose={() => setSelectedGRN(null)}
      />
    </div>
  )
}
